import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";


// Contexts & Hooks
import { useUserContext } from "../../context/UserContext";
import { ServiceRequestContext } from "./context/ServiceRequestContext";
import { usePaymentStatus } from "./hooks/usePaymentStatus";
import { useServiceContext } from "./context/ServiceContext";
import { useQuote } from "./hooks/useQuote";

// Utils & Services
import { RequestsService, ServicesService, PaymentsService, QuotesService, ServiceRequestOut } from "../../api";

// Child Components
import ServiceRequestMainContent from "./ServiceRequestMainContent";
import { logger } from "../../utils/logger";
import { buttonPrimary } from "@/styles/buttons";
import { extractServiceFee } from "@/components/form_renderer/utils/formUtils";
import { getFormId } from "@/components/form_renderer/FormMap";

type Role = "User" | "Admin";

const getPaymentStatus = (quote?: any): boolean => {
  if (!quote) return false;

  const paymentMode = quote?.payment?.mode;
  const payable = quote?.payment?.payable;

  return paymentMode === "full" && payable === 0;
};


const ServiceRequestDetails: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { requestId } = useParams<{ requestId: string }>();

  const { user } = useUserContext();
  const { setServiceId, setServiceConfig } = useServiceContext();

  const role: Role = user?.role_name === "Admin" ? "Admin" : "User";


  /* ================= STATE ================= */

  const [details, setDetails] = useState<ServiceRequestOut>();

  const [loadingRequest, setLoadingRequest] = useState(true);

  const { quote, loading: loadingQuote } = useQuote(requestId);
  // console.log("QUTOE IS ", JSON.stringify(quote));

  const [loadingService, setLoadingService] = useState(false);

  /* ================= FETCH REQUEST ================= */

  const fetchRequestDetails = async () => {
    if (!requestId) return;

    try {
      logger.info('ServiceRequestDetails', 'Fetching request details', { requestId });
      setLoadingRequest(true);

      const data =
        await RequestsService.getRequestByIdServiceRequestsRequestRequestIdGet(
          Number(requestId)
        );
      setDetails(data);
      // logger.info('ServiceRequestDetails', 'Request details fetched successfully', { requestId, serviceId: data.service_id, status: data.request_status_value });
    } catch (err) {
      logger.error('ServiceRequestDetails', 'Failed to fetch request details', { error: err, requestId });
      console.error("❌ Failed to fetch request details", err);
    } finally {
      setLoadingRequest(false);
      logger.info('ServiceRequestDetails', 'Request details fetch completed', { requestId });
    }
  };

  useEffect(() => {
    logger.info('ServiceRequestDetails', 'ServiceRequestDetails component mounted', { requestId, userId: user?.id });
    fetchRequestDetails();
  }, [requestId]);
  // console.log("REQUEST_DETAILS DETAILS", JSON.stringify(details));

  /* ================= FETCH SERVICE CONFIG ================= */

  useEffect(() => {
    if (!details?.service_id) return;

    setLoadingService(true);

    const fetchService = async () => {
      try {
        logger.info('ServiceRequestDetails', 'Fetching service config', { serviceId: details.service_id });
        const serviceDetail =
          await ServicesService.getServiceServicesServiceIdGet(
            Number(details.service_id)
          );

        // 🔥 Inject into context
        setServiceId(details.service_id);
        setServiceConfig(serviceDetail?.service_config || {});
        logger.info('ServiceRequestDetails', 'Service config loaded successfully', { serviceId: details.service_id });
      } catch (err) {
        logger.error('ServiceRequestDetails', 'Failed to fetch service config', { error: err, serviceId: details?.service_id });
        console.error("❌ Failed to fetch service config", err);
      } finally {
        setLoadingService(false);
      }
    };

    fetchService();
  }, [details?.service_id]);

  /* ================= FETCH QUOTE ================= */


  useEffect(() => {
    if (quote) {
      // console.log("Fetched quote:", quote);
      // console.log("Payment status:", getPaymentStatus(quote));
    }
  }, [quote]);


  /* ================= PAYMENT STATUS ================= */

  const {
    payments,
    status: paymentStatus,
    totalPaid,
    totalPayable,
    loading: paymentLoading,
  } = usePaymentStatus(requestId || "");

  /* ================= DERIVED ================= */

  const serviceFee = useMemo(() => {
    return details ? extractServiceFee(details.form_data) : 0;
  }, [details]);

  const schemaId = useMemo(() => {
    return details?.service_id
      ? getFormId(Number(details.service_id), "ServiceRequest")
      : null;
  }, [details?.service_id]);

  const isCancellationFlow =
    details?.request_status_value === "CANCELLATION" ||
    [
      "Cancellation Requested",
      "Cancellation Approved",
      "Refund Initiated",
      "Refund Processed",
    ].includes(details?.request_sub_status_value ?? "");


  /* ================= ACTIONS ================= */

  const navigateToEdit = () => {
    logger.info('ServiceRequestDetails', 'Navigating to edit request', { requestId: details?.id, serviceId: details?.service_id, schemaId });
    navigate(
      `/service-requests/edit/${details?.service_id}/${schemaId}/${details?.id}`,
      {
        state: {
          from: location.pathname,
          requestDetails: details,
        },
      }
    );
  };

  const handleMakePayment = () => {
    logger.info('ServiceRequestDetails', 'Initiating payment flow', { requestId: details?.id, serviceFee });
    navigate(`/home/service-requests/${details?.id}/review-order`, {
      state: {
        serviceRequestId: details?.id,
        serviceFee,
        parentPath: location.pathname + location.search,
        prefill: {
          phone: user?.phone,
          email: user?.email,
        },
      },
    });
  };

  const handleResumeChange = (newUrl: string | null) => {
    logger.debug('ServiceRequestDetails', 'Resume URL changed', { requestId: details?.id, newUrl });
    setDetails((prev: any) => ({
      ...prev,
      form_data: {
        ...prev.form_data,
        Resume: { ...prev.form_data?.Resume, resume_url: newUrl },
      },
    }));
  };

  const handleJDChange = (newUrl: string | null) => {
    logger.debug('ServiceRequestDetails', 'Job Description URL changed', { requestId: details?.id, newUrl });
    setDetails((prev: any) => ({
      ...prev,
      form_data: {
        ...prev.form_data,
        JobDescription: { ...prev.form_data?.JobDescription, jd_url: newUrl },
      },
    }));
  };

  const handleReportsChange = (newReports: any[]) => {
    logger.debug('ServiceRequestDetails', 'Reports updated', { requestId: details?.id, reportsCount: newReports.length });
    setDetails((prev: any) => ({
      ...prev,
      form_data: { ...prev.form_data, Reports: newReports },
    }));
  };

  /* ================= LOADING ================= */

  if (
    loadingRequest ||
    loadingService ||
    loadingQuote ||
    paymentLoading
  ) {
    return <p>Loading...</p>;
  }

  if (!details) return <p>No details found.</p>;

  /* ================= UI ================= */

  const isPaymentComplete = quote ? getPaymentStatus(quote) : false;
  // console.log("ISPAYMENT COMPLETE = ", isPaymentComplete);



  return (
    <ServiceRequestContext.Provider
      value={{ details, setDetails }}
    >
      <div className="w-full mx-auto sm:p-4 md:p-6 space-y-6 sm:space-y-8">
        <div className="bg-gray-150 border rounded-lg shadow p-4 space-y-1">

          {/* HEADER */}
          {/* <div className="flex justify-between items-center p-4 mb-4"> */}
            {/* <h2 className="text-lg text-black font-bold">
              {details?.request_label}
            </h2> */}

            {/* <div className="flex items-center gap-4 p-4">
              <button
                className={buttonPrimary}
                onClick={() => navigate("/dashboard")}
              >
                Back
              </button>

              <button
                className={buttonPrimary}
                onClick={handleRequestEdit}
              >
                Edit
              </button>
            </div> */}
          {/* </div> */}

          {/* </div> */}
          {/* MAIN CONTENT */}
          <ServiceRequestMainContent
            isCancellationFlow={isCancellationFlow}
            isPaymentComplete={isPaymentComplete}
            loadingQuote={loadingQuote}
            quote={quote}
            paymentStatus={paymentStatus}
            adminId={user?.id ?? 0}
            totalPaid={totalPaid ?? 0}
            totalPayable={totalPayable ?? 0}
            payments={payments}
            role={role}
            requestId={requestId || ""}
            details={details}
            onResumeChange={handleResumeChange}
            onJDChange={handleJDChange}
            onReportsChange={handleReportsChange}
            onRefresh={fetchRequestDetails}
          />
        </div>
      </div>
    </ServiceRequestContext.Provider>
  );
};

export default ServiceRequestDetails;

